// src/components/trades/TradeAnalysisModal.js
import React, { useState, useEffect } from 'react';
import { leaguesAPI } from '../../services/apiService';
import { dynastyTheme } from '../../services/colorService';
import { X, Loader, TrendingUp } from 'lucide-react';
import TradeSummary from './analysis/TradeSummary';
import SalaryCapSection from './analysis/SalaryCapSection';
import RosterCompositionSection from './analysis/RosterCompositionSection';
import PlayerStatsSection from './analysis/PlayerStatsSection';
import StandingsProjectionSection from './analysis/StandingsProjectionSection';
import WarningsSection from './analysis/WarningsSection';

const TradeAnalysisModal = ({ 
  isOpen, 
  onClose, 
  leagueId,
  receivingTeamId,
  proposingTeamGives,
  receivingTeamGives 
}) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAnalysis();
    }
  }, [isOpen]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await leaguesAPI.analyzeTrade(leagueId, {
        receiving_team_id: receivingTeamId,
        proposing_team_gives: proposingTeamGives,
        receiving_team_gives: receivingTeamGives
      });
      
      if (response.success) {
        setAnalysis(response);
      } else {
        setError(response.error || 'Failed to analyze trade');
      }
    } catch (err) {
      setError(err.message || 'Failed to analyze trade');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const LoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader className={`w-12 h-12 animate-spin ${dynastyTheme.classes.text.primary} mb-4`} />
      <p className="text-gray-300 text-lg">Analyzing trade...</p>
      <p className="text-gray-500 text-sm mt-2">Calculating impact on salary cap, roster, and standings</p>
    </div>
  );

  const ErrorState = ({ error }) => (
    <div className={`p-6 rounded-lg border ${dynastyTheme.classes.border.error} bg-red-500/10`}>
      <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.error} mb-2`}>Analysis Failed</h3>
      <p className={`${dynastyTheme.classes.text.error} opacity-90`}>{error}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black bg-opacity-30">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkCard}`}>
          <h2 className={`text-xl font-bold ${dynastyTheme.classes.text.primary} flex items-center gap-2`}>
            <TrendingUp className="w-6 h-6" />
            Trade Analysis
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading && <LoadingState />}
          {error && <ErrorState error={error} />}
          {analysis && (
            <>
              <TradeSummary summary={analysis.summary} />
              <SalaryCapSection data={analysis.salary_cap_impact} />
              <RosterCompositionSection data={analysis.roster_composition} />
              <PlayerStatsSection
                data={{
                  ...analysis.player_comparison,
                  scoring_categories: analysis.scoring_categories,
                  stats_season: analysis.stats_season
                }}
              />
              <StandingsProjectionSection data={analysis.standings_projection} />
              {analysis.warnings?.length > 0 && <WarningsSection warnings={analysis.warnings} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradeAnalysisModal;
